import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../../../config/api';
import { Search, Plus, Edit, Trash2, Eye, EyeOff, FileText } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

interface AnalysisTemplate {
  id: number;
  name: string;
  description?: string;
  structure_json: any;
  applicable_tags?: string;
  status: number;
  created_by?: number;
  created_at: string;
  updated_at: string;
}

interface FormData {
  name: string;
  description: string;
  structure_json: string;
  applicable_tags: string;
  status: number;
}

const AnalysisTemplateManagement: React.FC = () => {
  const [templates, setTemplates] = useState<AnalysisTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<AnalysisTemplate | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    structure_json: '',
    applicable_tags: '',
    status: 1
  });
  const [submitting, setSubmitting] = useState(false);

  // 预设的分析模板结构示例
  const templateExamples = [
    {
      name: 'SWOT分析',
      structure: {
        sections: [
          { key: 'strengths', title: '优势(Strengths)', type: 'textarea', required: true },
          { key: 'weaknesses', title: '劣势(Weaknesses)', type: 'textarea', required: true },
          { key: 'opportunities', title: '机会(Opportunities)', type: 'textarea', required: true },
          { key: 'threats', title: '威胁(Threats)', type: 'textarea', required: true }
        ]
      }
    },
    {
      name: '5W1H分析',
      structure: {
        sections: [
          { key: 'what', title: 'What(什么)', type: 'textarea', required: true },
          { key: 'who', title: 'Who(谁)', type: 'textarea', required: true },
          { key: 'when', title: 'When(何时)', type: 'textarea', required: true },
          { key: 'where', title: 'Where(何地)', type: 'textarea', required: true },
          { key: 'why', title: 'Why(为什么)', type: 'textarea', required: true },
          { key: 'how', title: 'How(如何)', type: 'textarea', required: true }
        ]
      }
    },
    {
      name: '因果分析',
      structure: {
        sections: [
          { key: 'problem', title: '问题描述', type: 'textarea', required: true },
          { key: 'direct_causes', title: '直接原因', type: 'textarea', required: true },
          { key: 'root_causes', title: '根本原因', type: 'textarea', required: true },
          { key: 'effects', title: '影响结果', type: 'textarea', required: true },
          { key: 'solutions', title: '解决方案', type: 'textarea', required: true }
        ]
      }
    }
  ];

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/news-admin-manage?module=templates&action=list`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        // 确保 result.data 是数组，如果有分页结构则取 data.data
        const templatesData = result.data?.data || result.data || [];
        setTemplates(Array.isArray(templatesData) ? templatesData : []);
      } else {
        console.error('获取分析模板失败:', result.error);
        setTemplates([]); // 确保设置为空数组
      }
    } catch (error) {
      console.error('获取分析模板失败:', error);
      setTemplates([]); // 确保设置为空数组
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    try {
      setSubmitting(true);
      
      // 验证JSON格式
      let structureJson;
      try {
        structureJson = JSON.parse(formData.structure_json);
      } catch (error) {
        alert('模板结构JSON格式不正确，请检查后重试');
        return;
      }

      if (editingTemplate) {
        // 更新模板
        const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/news-admin-manage?module=templates&action=update&id=${editingTemplate.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: formData.name,
            description: formData.description,
            structure_json: structureJson,
            applicable_tags: formData.applicable_tags,
            status: formData.status
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        if (!result.success) {
          console.error('更新分析模板失败:', result.error);
          alert('更新失败：' + result.error);
          return;
        }
      } else {
        // 创建模板
        const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/news-admin-manage?module=templates&action=create`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: formData.name,
            description: formData.description,
            structure_json: structureJson,
            applicable_tags: formData.applicable_tags,
            status: formData.status
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        if (!result.success) {
          console.error('创建分析模板失败:', result.error);
          alert('创建失败：' + result.error);
          return;
        }
      }

      await fetchTemplates();
      setShowForm(false);
      setEditingTemplate(null);
      resetForm();
    } catch (error) {
      console.error('保存分析模板失败:', error);
      alert('保存失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('确定要删除这个分析模板吗？')) return;

    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/news-admin-manage?module=templates&action=delete&id=${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        console.error('删除分析模板失败:', result.error);
        alert('删除失败：' + result.error);
        return;
      }

      await fetchTemplates();
    } catch (error) {
      console.error('删除分析模板失败:', error);
      alert('删除失败，请重试');
    }
  };

  const handleEdit = (template: AnalysisTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      structure_json: JSON.stringify(template.structure_json, null, 2),
      applicable_tags: template.applicable_tags || '',
      status: template.status
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      structure_json: '',
      applicable_tags: '',
      status: 1
    });
  };

  const useTemplateExample = (example: any) => {
    setFormData({
      ...formData,
      name: example.name,
      structure_json: JSON.stringify(example.structure, null, 2)
    });
  };

  const filteredTemplates = Array.isArray(templates) ? templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || template.status.toString() === statusFilter;
    return matchesSearch && matchesStatus;
  }) : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">分析模板管理</h2>
        </div>
        <div className="p-6">
          {/* 搜索和筛选 */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="搜索模板名称..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">全部状态</option>
              <option value="1">启用</option>
              <option value="0">禁用</option>
            </select>
            <button
              onClick={() => {
                setEditingTemplate(null);
                resetForm();
                setShowForm(true);
              }}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <Plus className="w-4 h-4 mr-2" />
              添加模板
            </button>
          </div>

          {/* 模板列表 */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    名称
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    描述
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    适用标签
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    状态
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    创建时间
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTemplates.map((template) => (
                  <tr key={template.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      <div className="flex items-center">
                        <FileText className="w-4 h-4 mr-2 text-blue-500" />
                        {template.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div className="max-w-xs truncate">
                        {template.description || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {template.applicable_tags ? (
                        <div className="flex flex-wrap gap-1">
                          {template.applicable_tags.split(',').map((tag, index) => (
                            <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                              {tag.trim()}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        template.status === 1 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {template.status === 1 ? (
                          <><Eye className="w-3 h-3 mr-1" />启用</>
                        ) : (
                          <><EyeOff className="w-3 h-3 mr-1" />禁用</>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(template.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEdit(template)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(template.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredTemplates.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">暂无分析模板数据</p>
            </div>
          )}
        </div>
      </div>

      {/* 表单弹窗 */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingTemplate ? '编辑分析模板' : '添加分析模板'}
              </h3>
              
              {/* 模板示例 */}
              {!editingTemplate && (
                <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">快速开始 - 选择模板示例：</h4>
                  <div className="flex flex-wrap gap-2">
                    {templateExamples.map((example, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => useTemplateExample(example)}
                        className="px-3 py-1 text-xs bg-blue-100 text-blue-800 rounded-full hover:bg-blue-200"
                      >
                        {example.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      模板名称 *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="请输入模板名称"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      状态
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value={1}>启用</option>
                      <option value={0}>禁用</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    描述
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="请输入模板描述"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    适用标签
                  </label>
                  <input
                    type="text"
                    value={formData.applicable_tags}
                    onChange={(e) => setFormData({ ...formData, applicable_tags: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="请输入适用标签，多个标签用逗号分隔"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    模板结构 (JSON格式) *
                  </label>
                  <textarea
                    required
                    value={formData.structure_json}
                    onChange={(e) => setFormData({ ...formData, structure_json: e.target.value })}
                    rows={12}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                    placeholder='请输入JSON格式的模板结构，例如：\n{\n  "sections": [\n    {\n      "key": "analysis",\n      "title": "分析内容",\n      "type": "textarea",\n      "required": true\n    }\n  ]\n}'
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    提示：sections数组中每个对象包含key(字段名)、title(显示标题)、type(输入类型)、required(是否必填)等属性
                  </p>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingTemplate(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    取消
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                  >
                    {submitting ? '保存中...' : '保存'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalysisTemplateManagement;